import classNames from 'classnames'
import { ArrowRight } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { setCurrentProfile } from '../../actions/user'
import { useSavedFiltersStore } from '../../hooks/useSavedFiltersStore'
import { useUser } from '../../hooks/useUser'
import type { SavedFilter } from '../../packages/blueprint-components/src/useFilter'
import { type EntityId, idKey } from '../../types/constants'
import { Dialog } from '../ui/dialog'
import { sections, settingsSections } from '../nav-v2/items'
import { useActiveFilterStore } from './active-filter-store-context'

export interface CommandPaletteProps {
    isOpen: boolean
    onClose: () => void
}

interface ProfileRow {
    kind: 'profile'
    key: string
    profileId: EntityId
    label: string
    detail: string
    haystack: string
}

interface NavigationRow {
    kind: 'navigation'
    key: string
    to: string
    label: string
    haystack: string
}

interface FilterRow {
    kind: 'filter'
    key: string
    filter: SavedFilter
    label: string
    haystack: string
}

interface ClearFiltersRow {
    kind: 'clearFilters'
    key: string
    label: string
    haystack: string
}

type Row = NavigationRow | ProfileRow | FilterRow | ClearFiltersRow

const INPUT_ID = 'command-palette-query'
const CLEAR_FILTERS_ROW: ClearFiltersRow = {
    kind: 'clearFilters',
    key: 'clear-filters',
    label: 'Clear all filters',
    haystack: 'clear all filters',
}

const fuzzyMatches = (query: string, haystack: string) => {
    const queryCharacters = query.toLowerCase().match(/[a-z0-9]/g) ?? []
    let queryIndex = 0

    for (const character of haystack.toLowerCase()) {
        if (character === queryCharacters[queryIndex]) {
            queryIndex += 1
        }
    }

    return queryIndex === queryCharacters.length
}

const toProfileRow = (profile: Models.Profile): ProfileRow => {
    const label = profile.nickname ?? profile.name ?? ''
    const entityId = profile.entity_id ?? ''
    const profileId = idKey(profile.profile_id)

    return {
        kind: 'profile',
        key: `profile-${profileId}`,
        profileId: profile.profile_id,
        label: `Select profile ${label}`,
        detail: entityId,
        haystack: `select profile ${label} ${entityId} ${profileId}`.toLowerCase(),
    }
}

const toFilterRow = (filter: SavedFilter): FilterRow => ({
    kind: 'filter',
    key: `filter-${idKey(filter.id)}`,
    filter,
    label: `Load filter ${filter.name}`,
    haystack: `load filter ${filter.name} ${filter.table_name}`.toLowerCase(),
})

export const CommandPalette: React.FC<CommandPaletteProps> = observer(({ isOpen, onClose }) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { search } = useLocation()
    const { profiles, user } = useUser()
    const savedFiltersStore = useSavedFiltersStore()
    const activeStore = useActiveFilterStore()
    const [query, setQuery] = useState('')
    const [highlight, setHighlight] = useState(0)
    const inputRef = useRef<HTMLInputElement | null>(null)
    const resultsRef = useRef<HTMLDivElement | null>(null)
    const returnFocusRef = useRef<HTMLElement | null>(null)
    const hasOpenedRef = useRef(false)

    if (isOpen && !hasOpenedRef.current) {
        returnFocusRef.current = document.activeElement as HTMLElement | null
        hasOpenedRef.current = true
    }

    const activeTableName = activeStore?.savedFiltersTableName ?? null
    const filters = activeTableName ? savedFiltersStore.filtersFor(activeTableName) : []

    useEffect(() => {
        if (isOpen && activeTableName) {
            savedFiltersStore.load(activeTableName)
        }
    }, [isOpen, activeTableName, savedFiltersStore])

    const matchesQuery = (row: Row) => fuzzyMatches(query, row.haystack)

    const navigationRows = useMemo(() => {
        const isAdmin = !!user?.is_admin
        return [...sections, ...settingsSections]
            .filter((section) => (section.visible ? section.visible({ isAdmin, user }) : true))
            .flatMap((section) => section.items)
            .filter((item) => (item.visible ? item.visible({ isAdmin, user }) : true))
            .map<NavigationRow>((item) => ({
                kind: 'navigation',
                key: `navigation-${item.to}`,
                to: item.to,
                label: `Go to ${item.label}`,
                haystack: `go to ${item.label}`.toLowerCase(),
            }))
            .filter(matchesQuery)
    }, [user, query])

    const thisViewRows = useMemo(() => {
        if (!activeTableName) {
            return []
        }
        return filters
            .filter((filter) => filter.table_name === activeTableName)
            .map(toFilterRow)
            .filter(matchesQuery)
    }, [filters, activeTableName, query])

    const clearFiltersRows: ClearFiltersRow[] =
        activeStore &&
        Object.keys(activeStore.currentFilters).length > 0 &&
        matchesQuery(CLEAR_FILTERS_ROW)
            ? [CLEAR_FILTERS_ROW]
            : []

    const profileRows = useMemo(() => {
        return (profiles ?? [])
            .filter((profile) => profile.managed === true)
            .map(toProfileRow)
            .filter(matchesQuery)
    }, [profiles, query])

    // One flat list so a single highlight index walks every section in order.
    const rows: Row[] = useMemo(
        () => [...navigationRows, ...clearFiltersRows, ...thisViewRows, ...profileRows],
        [navigationRows, clearFiltersRows, thisViewRows, profileRows],
    )

    const highlightedIndex = rows.length === 0 ? -1 : Math.min(highlight, rows.length - 1)

    useEffect(() => {
        if (!isOpen) {
            setQuery('')
        }
        setHighlight(0)
    }, [isOpen])

    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus()
        }
    }, [isOpen])

    useEffect(() => {
        if (isOpen && highlightedIndex >= 0) {
            resultsRef.current
                ?.querySelector<HTMLElement>(`[data-command-palette-index="${highlightedIndex}"]`)
                ?.scrollIntoView?.({ block: 'nearest' })
        }
    }, [isOpen, highlightedIndex, query])

    useEffect(() => {
        if (!isOpen && hasOpenedRef.current) {
            hasOpenedRef.current = false
            setTimeout(() => returnFocusRef.current?.focus())
        }
    }, [isOpen])

    const select = (row: Row | undefined) => {
        if (!row) {
            return
        }

        if (row.kind === 'navigation') {
            navigate({ pathname: row.to, search })
            onClose()
            return
        }

        if (row.kind === 'profile') {
            dispatch(setCurrentProfile(row.profileId))
            onClose()
            return
        }

        if (row.kind === 'clearFilters') {
            activeStore?.clear()
            onClose()
            return
        }

        activeStore?.applySavedFilter(row.filter)
        onClose()
    }

    /** The next row index in `direction`, clamped at either end. */
    const step = (direction: 1 | -1) => {
        if (highlightedIndex < 0) {
            return 0
        }
        return Math.min(Math.max(highlightedIndex + direction, 0), rows.length - 1)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setHighlight(step(1))
            return
        }

        if (event.key === 'ArrowUp') {
            event.preventDefault()
            setHighlight(step(-1))
            return
        }

        if (event.key === 'Enter') {
            event.preventDefault()
            select(highlightedIndex >= 0 ? rows[highlightedIndex] : undefined)
        }
    }

    const renderRow = (row: Row, index: number) => {
        const prefix =
            row.kind === 'navigation'
                ? 'Go to '
                : row.kind === 'profile'
                  ? 'Select profile '
                  : row.kind === 'filter'
                    ? 'Load filter '
                    : ''
        return (
            <button
                key={row.key}
                type="button"
                role="option"
                aria-label={row.label}
                aria-selected={index === highlightedIndex}
                data-command-palette-index={index}
                onClick={() => select(row)}
                onMouseEnter={() => setHighlight(index)}
                className={classNames(
                    'flex w-full items-center justify-between gap-3 px-4 py-2 text-left text-[14px]',
                    index === highlightedIndex ? 'bg-[#F1F5F9] text-[#1c2127]' : 'bg-transparent',
                    'text-[#1c2127]',
                )}
            >
                <span className="flex min-w-0 items-center gap-2 truncate">
                    {row.kind === 'navigation' && (
                        <ArrowRight aria-hidden size={14} className="shrink-0" />
                    )}
                    <span className="truncate">
                        {prefix && <span className="text-[#8A8F98]">{prefix}</span>}
                        {row.label.slice(prefix.length)}
                    </span>
                </span>
                {row.kind === 'profile' && (
                    <span className="shrink-0 text-[11px] text-[#8A8F98]">{row.detail}</span>
                )}
            </button>
        )
    }

    const sectionHeading = (text: string) => (
        <div className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-[#8A8F98]">
            {text}
        </div>
    )

    return (
        <Dialog isOpen={isOpen} onClose={onClose} title={undefined} className="w-[560px] p-0">
            <div className="flex flex-col bg-white rounded-[4px]">
                <label className="sr-only" htmlFor={INPUT_ID}>
                    Search profiles and saved filters
                </label>
                <input
                    id={INPUT_ID}
                    ref={inputRef}
                    type="text"
                    autoComplete="off"
                    placeholder="Search profiles and saved filters…"
                    className="w-full border-0 border-b border-[#E5E7EB] px-4 py-3 text-[14px] text-[#1c2127] focus:outline-none"
                    value={query}
                    onChange={(event) => {
                        setQuery(event.target.value)
                        setHighlight(0)
                    }}
                    onKeyDown={handleKeyDown}
                />

                <div
                    ref={resultsRef}
                    role="listbox"
                    aria-label="Results"
                    className="max-h-[292px] overflow-y-auto pb-2"
                >
                    {navigationRows.length > 0 && sectionHeading('Navigation')}
                    {navigationRows.map((row, index) => renderRow(row, index))}

                    {clearFiltersRows.length > 0 && sectionHeading('Actions')}
                    {clearFiltersRows.map((row, index) =>
                        renderRow(row, navigationRows.length + index),
                    )}

                    {thisViewRows.length > 0 && sectionHeading('Saved filters')}
                    {thisViewRows.map((row, index) =>
                        renderRow(row, navigationRows.length + clearFiltersRows.length + index),
                    )}

                    {sectionHeading('Profiles')}
                    {profileRows.length === 0 ? (
                        <div className="px-4 py-3 text-[13px] text-[#8A8F98]">
                            No matching profiles
                        </div>
                    ) : (
                        profileRows.map((row, index) =>
                            renderRow(
                                row,
                                navigationRows.length +
                                    clearFiltersRows.length +
                                    thisViewRows.length +
                                    index,
                            ),
                        )
                    )}
                </div>
            </div>
        </Dialog>
    )
})
